import type { ApiClient } from '@ghp/api-client'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  stripSearchParams,
  type RouterHistory,
} from '@tanstack/react-router'
import { forgotPasswordRoute } from '../features/auth/routes/forgot-password'
import { loginRoute } from '../features/auth/routes/login'
import { resetPasswordRoute } from '../features/auth/routes/reset-password'
import {
  CLIENT_LIST_DEFAULTS,
  clientListSearchSchema,
  type ClientListSearch,
} from '../features/clients/lib/client-list-search'
import {
  CLIENT_EXPORT_DEFAULTS,
  clientExportSearchSchema,
  type ClientExportSearch,
} from '../features/exports/lib/client-export-search'
import {
  ARCHIVE_DEFAULTS,
  archiveSearchSchema,
  type ArchiveSearch,
} from '../features/inquiries/lib/archive-search'
import {
  DASHBOARD_DEFAULTS,
  dashboardSearchSchema,
  INQUIRY_LIST_DEFAULTS,
  inquiryListSearchSchema,
  type InquiryListSearch,
  type DashboardSearch,
} from '../features/inquiries/lib/inquiry-list-search'
import {
  REMINDER_DEFAULTS,
  reminderSearchSchema,
  type ReminderSearch,
} from '../features/inquiries/lib/reminder-search'
import {
  TEAMS_DEFAULTS,
  teamsSearchSchema,
  type TeamsSearch,
} from '../features/planning/lib/teams-search'
import {
  PLANNING_DEFAULTS,
  planningSearchSchema,
  type PlanningSearch,
} from '../features/planning/lib/planning-search'
import {
  INSPECTION_LIST_DEFAULTS,
  inspectionListSearchSchema,
  type InspectionListSearch,
} from '../features/validation/lib/inspection-list-search'
import { importSearchSchema, type ImportSearch } from '../features/inquiries/lib/import-search'
import {
  reportRulesSearchSchema,
  type ReportRulesSearch,
} from '../features/inspections/lib/report-rules-search'
import {
  inspectionEditorSearchSchema,
  type InspectionEditorSearch,
} from '../features/validation/lib/inspection-editor-search'
import { rootRoute, shellRoute } from './routes/root'

/**
 * Arbre de routes — **routage par code**, pas par fichiers.
 *
 * Le routage par fichiers de TanStack Router suppose son plugin Vite et un
 * `routeTree.gen.ts` **généré**. Or `pnpm typecheck` exécute `tsc` sans passer par Vite :
 * le fichier généré devrait donc être soit committé (artefact dérivé versionné), soit
 * produit par une étape de génération ajoutée avant le typecheck en CI. Une pièce mobile
 * de plus, pour un bénéfice — la convention de nommage — qui ne change rien à ce qui
 * compte ici : les `search params` typés se déclarent avec `validateSearch` **de la même
 * façon** dans les deux modes.
 *
 * Ce choix est réversible : les routes restent une par fichier, avec le composant à côté
 * de sa déclaration — exactement la forme qu'attend le générateur si l'on bascule.
 * À rouvrir quand le nombre d'écrans rendra l'assemblage manuel pénible.
 *
 * **Les déclarations vivent ici, les écrans dans leur module.** `lazyRouteComponent`
 * ne tient sa promesse — un fragment par écran, chargé au survol grâce à
 * `defaultPreload: 'intent'` — que si rien du bundle d'entrée n'importe le module de
 * l'écran. Une route déclarée à côté de son composant l'y ramènerait aussitôt : c'est
 * pourquoi les modules d'écran exportent leur composant et retrouvent leurs `search`
 * et `params` typés par `getRouteApi(<id>)`.
 *
 * Seul le **modèle de filtres** (`lib/inquiry-list-search`) reste chargé d'emblée :
 * `validateSearch` s'exécute à la navigation, avant que le composant ne soit résolu.
 */
const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  // Les écrans de travail vivent sous la coquille et sa garde de session.
  shellRoute.addChildren([
    // Pilotage
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/',
      validateSearch: (search: Record<string, unknown>): DashboardSearch =>
        dashboardSearchSchema.parse(search),
      search: {
        // « Tous les marchés » n'apparaît pas dans l'URL : `/` reste `/`.
        middlewares: [stripSearchParams<DashboardSearch>(DASHBOARD_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/dashboard'),
        'Dashboard',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/inquiries',
      validateSearch: (search: Record<string, unknown>): InquiryListSearch =>
        inquiryListSearchSchema.parse(search),
      search: {
        // Un filtre à sa valeur par défaut n'apparaît pas dans l'URL : `/inquiries` reste
        // `/inquiries`, et une adresse copiée ne porte que ce qui a été choisi.
        middlewares: [stripSearchParams<InquiryListSearch>(INQUIRY_LIST_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/inquiries'),
        'InquiriesRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/planning',
      validateSearch: (search: Record<string, unknown>): PlanningSearch =>
        planningSearchSchema.parse(search),
      search: {
        // `/planning` reste `/planning` sur la semaine courante et la vue par défaut : seul un
        // choix s'écrit. ⚠️ La semaine par défaut est **absente** de l'URL, pas figée dedans — un
        // lien copié un lundi ouvrirait sinon cette semaine-là pour toujours.
        middlewares: [stripSearchParams<PlanningSearch>(PLANNING_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/planning/routes/planning'),
        'Planning',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/clients',
      validateSearch: (search: Record<string, unknown>): ClientListSearch =>
        clientListSearchSchema.parse(search),
      search: {
        // `/clients` reste `/clients` sur la vue par défaut : seule une vue choisie s'écrit.
        middlewares: [stripSearchParams<ClientListSearch>(CLIENT_LIST_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/clients/routes/clients'),
        'ClientsRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      // ⚠️ **Avant `/clients/$clientId`** : sans cet ordre, `new` serait pris pour un
      // identifiant de client et la création ouvrirait une fiche introuvable.
      path: '/clients/new',
      component: lazyRouteComponent(
        () => import('../features/clients/routes/new-client'),
        'NewClientRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      // ⚠️ **Sous `/clients`, et non sur une route de premier niveau** : la fiche est le détail du
      // répertoire, et le chemin dit d'où l'on vient — ce que le bouton « Retour » suit.
      path: '/clients/$clientId',
      component: lazyRouteComponent(
        () => import('../features/clients/routes/client-sheet'),
        'ClientSheetRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/teams',
      validateSearch: (search: Record<string, unknown>): TeamsSearch =>
        teamsSearchSchema.parse(search),
      search: {
        // `/teams` reste `/teams` sur l'onglet par défaut : seul un onglet choisi s'écrit.
        middlewares: [stripSearchParams<TeamsSearch>(TEAMS_DEFAULTS)],
      },
      component: lazyRouteComponent(() => import('../features/planning/routes/teams'), 'Teams'),
    }),
    // ⚠️ **Déclarée avant `/teams/$teamId`, et le chemin est littéral** : le prototype fait de la
    // création d'équipe une page pleine (`data-nav="team-create"`), pas une boîte. `new` doit
    // rester **devant** le paramètre, sans quoi `/teams/new` s'y engouffrerait et la fiche
    // chercherait une équipe dont l'identifiant serait la chaîne « new ».
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/teams/new',
      component: lazyRouteComponent(
        () => import('../features/planning/routes/new-team'),
        'NewTeam',
      ),
    }),
    // La fiche d'une équipe — ce qu'ouvre une tuile de la grille (`data-open-team`).
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/teams/$teamId',
      component: lazyRouteComponent(
        () => import('../features/planning/routes/team-detail'),
        'TeamDetailRoute',
      ),
    }),
    // La fiche d'un agent — ce qu'ouvre une carte de l'onglet « Membres » (`data-open-member`).
    //
    // ⚠️ **Trois segments, donc aucune collision avec `/teams/$teamId`** : ce n'est pas l'ordre
    // qui les sépare ici mais leur forme. Et la fiche n'est **pas** imbriquée sous celle de
    // l'équipe, contrairement à ce que la hiérarchie suggère : un agent peut n'appartenir à
    // aucune équipe (M6), et sa fiche serait alors inatteignable.
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/teams/members/$userId',
      component: lazyRouteComponent(
        () => import('../features/planning/routes/member-detail'),
        'MemberDetailRoute',
      ),
    }),
    // Traitement
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/imports',
      validateSearch: (search: Record<string, unknown>): ImportSearch =>
        importSearchSchema.parse(search),
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/imports'),
        'Imports',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/inspections',
      validateSearch: (search: Record<string, unknown>): InspectionListSearch =>
        inspectionListSearchSchema.parse(search),
      search: {
        // Un filtre à sa valeur par défaut n'apparaît pas dans l'URL : `/inspections` reste
        // `/inspections`, et une adresse copiée ne porte que ce qui a été choisi.
        middlewares: [stripSearchParams<InspectionListSearch>(INSPECTION_LIST_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/validation/routes/inspections'),
        'InspectionsRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/inspections/$inspectionId',
      // L'onglet d'ouverture (`?tab=diagram`) : le lien d'un scénario de recette ouvre « Schéma ».
      validateSearch: (search: Record<string, unknown>): InspectionEditorSearch =>
        inspectionEditorSearchSchema.parse(search),
      component: lazyRouteComponent(
        () => import('../features/validation/routes/inspection-editor'),
        'InspectionEditorRoute',
      ),
    }),
    // La liste des passages sans contrôle, et les courriers qu'on en tire. Voisine de
    // « Relances » dans le bandeau comme dans le prototype : les deux disent qu'un dossier
    // n'avance pas, l'une par le délai, l'autre par une porte close.
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/passage-notices',
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/passage-notices-route'),
        'PassageNoticesRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/reminders',
      validateSearch: (search: Record<string, unknown>): ReminderSearch =>
        reminderSearchSchema.parse(search),
      search: {
        // `/reminders` reste `/reminders` sur la vue par défaut : seul un choix s'écrit.
        middlewares: [stripSearchParams<ReminderSearch>(REMINDER_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/reminders'),
        'Reminders',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/exports',
      validateSearch: (search: Record<string, unknown>): ClientExportSearch =>
        clientExportSearchSchema.parse(search),
      search: {
        // `/exports` reste `/exports` sur l'onglet par défaut : seul un choix s'écrit.
        middlewares: [stripSearchParams<ClientExportSearch>(CLIENT_EXPORT_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/exports/routes/client-exports'),
        'ClientExports',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/archives',
      validateSearch: (search: Record<string, unknown>): ArchiveSearch =>
        archiveSearchSchema.parse(search),
      search: {
        // `/archives` reste `/archives` tant qu'aucun filtre n'est choisi : une adresse copiée
        // ne porte que la recherche et l'équipe qu'on a réellement posées.
        middlewares: [stripSearchParams<ArchiveSearch>(ARCHIVE_DEFAULTS)],
      },
      component: lazyRouteComponent(
        () => import('../features/inquiries/routes/archives'),
        'Archives',
      ),
    }),
    // Gestion
    createRoute({
      getParentRoute: () => shellRoute,
      // ⚠️ `/users`, et non `/user-roles` : la ressource est le compte, et les « rôles » du
      // libellé sont une colonne de cette ressource — pas une seconde entité. Le pendant côté
      // API est `GET /users`, transverse comme lui (M6).
      path: '/users',
      component: lazyRouteComponent(() => import('../features/users/routes/users'), 'UsersRoute'),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/team-accounts',
      component: lazyRouteComponent(
        () => import('../features/users/routes/team-accounts'),
        'TeamAccounts',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/report-rules',
      validateSearch: (search: Record<string, unknown>): ReportRulesSearch =>
        reportRulesSearchSchema.parse(search),
      component: lazyRouteComponent(
        () => import('../features/inspections/routes/report-rules'),
        'ReportRulesRoute',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/anomaly-rules',
      component: lazyRouteComponent(
        () => import('../features/inspections/routes/anomaly-rules'),
        'AnomalyRules',
      ),
    }),
    createRoute({
      getParentRoute: () => shellRoute,
      path: '/settings',
      component: lazyRouteComponent(
        () => import('../features/exports/routes/settings'),
        'Settings',
      ),
    }),
  ]),
])

/**
 * Fabrique du routeur — pas une instance exportée, pour la même raison que le
 * `QueryClient` : deux tests ne doivent pas partager l'état de navigation.
 *
 * @param api - Le client de l'API : la garde de `shellRoute` lit la session avec, avant
 *   qu'aucun composant ne soit monté.
 * @param history - Injecté par les tests (`createMemoryHistory`) ; en production,
 *   TanStack Router installe de lui-même l'historique du navigateur.
 */
export function createAppRouter(queryClient: QueryClient, api: ApiClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    // Rend le `QueryClient` et l'API disponibles aux `loader` et aux gardes de routes
    // (intégration D19/D21).
    context: { queryClient, api },
    // Précharge au survol / au focus : sur un outil de bureau, la navigation paraît
    // instantanée sans rien précharger d'inutile au démarrage.
    defaultPreload: 'intent',
    scrollRestoration: true,
    history,
  })
}

/**
 * Enregistrement du routeur auprès de TanStack Router : c'est ce qui rend `<Link to>`,
 * `useNavigate` et les `search params` typés **de bout en bout** dans toute l'app.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
